import {IWedEditorMain, OWedEditorMainConfig, WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {Action, IAction, IActionToggle} from "/@lib@/commons/actions.js";
import {WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";

export interface OWedEditorDiffBarConfig extends OWedEditorMainConfig {
	/**
	 * Affichage de la barre de diff :
	 * - none : pas de barre de diff disponible.
	 * - shown : barre de diff affichée en permanence.
	 * - available : barre de diff dispo mais masquée à l'init, affichable à la demande.
	 * - available-shown : barre de diff affichée par défaut, mais masquable
	 */
	diffBarMode?: 'none' | 'shown' | 'available' | 'available-shown'
}

export interface IWedEditorDiffBar extends IWedEditorMain {
	diffBar?: IWedDiffBar

	getDiffBar(): Promise<IWedDiffBar>
}

/** Agrégation pour le IWedEditor ajoutant la diffBar. */
export function AgDiffBarEditor(cls: any): any {
	const proto = cls.prototype as IWedEditorDiffBar;
	const superInit = proto.initialize;
	proto.initialize = function (this: IWedEditorDiffBar, config: OWedEditorDiffBarConfig) {
		superInit.call(this, config);
		proto.getDiffBar = getDiffBar;
		if (config.diffBarMode != 'none' && this.reg.isListFilled("wed.diffBar.actions")) {
			this.wedMgr.accelKeyMgr.addAccelKey("d", 'accel shift', ToggleDiffBar.SINGLETON);
			this.reg.addToList("actions:wed:commonbar:end", "diffBar", 1, ShowDiffBar.SINGLETON, 1);
			if (config.diffBarMode === 'shown' || config.diffBarMode === 'available-shown') {
				this.wedMgr.listeners.once("redrawAtEnd", () => {this.getDiffBar().then((diffBar) => diffBar?.show())});
			}
			this.wedMgr.listeners.on("redrawAtEnd", coordDiff);
			this.wedMgr.listeners.on("shown", coordDiff);
		}
		return this;
	};
	return cls;
}

function coordDiff(wedMgr: WedMgr) {
	const diffParams = (wedMgr.wedEditor.reg.env as IWedDiffCoordinatorPointer).wedDiffCoord?.getParams();
	if (diffParams) (wedMgr.wedEditor as IWedEditorDiffBar).getDiffBar()?.then((bar) => {
		return bar.setDiffParams(diffParams);
	});
}

async function getDiffBar(this: IWedEditorDiffBar): Promise<IWedDiffBar> {
	if (!this.diffBar) {
		await WEDLET.importDiffLib();
		if (!this.diffBar) this.diffBar = new (await import("/@back@/edit/wed/features/diffBar_.js")).WedDiffBar().init(this);
	}
	return this.diffBar;
}

/** Action d'affichage ou masquage de la diffBar. */
export class ToggleDiffBar extends Action<IWedEditorDiffBar> implements IActionToggle<IWedEditorDiffBar> {
	static SINGLETON = new ToggleDiffBar();

	constructor(id?: string) {
		super('diff' || id);
		this._label = "Comparer...";
		this._icon = "/@skin@/edit/wed/diff/diff.svg";
	}

	isVisible(ctx: IWedEditorDiffBar): boolean {
		if ((ctx.config as OWedEditorDiffBarConfig).diffBarMode === 'none' || (ctx.config as OWedEditorDiffBarConfig).diffBarMode === 'shown') return false;
		return super.isVisible(ctx);
	}

	isToggle(): this is IActionToggle<IWedEditorDiffBar> {return true}

	getDatas(api: 'toggle', ctx: IWedEditorDiffBar): boolean | null {
		return ctx.diffBar && !ctx.diffBar.hidden;
	}

	async execute(ctx: IWedEditorDiffBar, ev?: Event) {
		const bar = await ctx.getDiffBar();
		if (bar.hidden) bar.show();
		else bar.close();
	}
}

/** Action pour bouton masqué si la diffBar est déjà visible. */
export class ShowDiffBar extends ToggleDiffBar {
	static SINGLETON = new ShowDiffBar();

	isToggle(): this is IActionToggle<IWedEditorDiffBar> {return false}

	isVisible(ctx: IWedEditorDiffBar): boolean {
		if (ctx.diffBar && !ctx.diffBar.hidden) return false;
		return super.isVisible(ctx);
	}
}

/** Abstraction du widget pour la barre de diff. */
export interface IWedDiffBar extends HTMLElement {

	editor: IWedEditorDiffBar;

	currentPanel: IDiffBarPanel | null

	initParams: JWedDiffBarParams | null;

	getPanelFor(action: IDiffBarAction): IDiffBarPanel | null

	setCurrentPanel(panel: IDiffBarPanel | null): void

	getDiffParams(): JWedDiffBarParams | null

	setDiffParams(params: JWedDiffBarParams): Promise<void>

	setDiffStates(st: 'drawn' | 'computable' | 'notComputable'): void;

	show(): void

	close(): void
}

/** Action responsable de l'affichage d'un des panels de la diffBar. */
export interface IDiffBarAction extends IAction<IWedDiffBar> {
	execute(ctx: IWedDiffBar, ev?: Event): Promise<void>
}


/** Données pour initialiser un panneau de recherche. Les autres paramètres sont dépendants de chaque type de panneau de recherche. */
export interface JWedDiffBarParams {
	/** Id du IDiffBarAction cible */
	diffId: string
}


/** Panel construit par IDiffBarAction et affecté à la diffBar via WedDiffBar.setCurrentPanel(). */
export interface IDiffBarPanel extends HTMLElement {

	/** Action à l'origine de la création de ce panel.*/
	action: IDiffBarAction

	/** Connecte l'éditeur à ce panneau de recherche. Injecte alors les objets recherchés. */
	connectDoc(): void

	/** Déconnecte le panneau de recherche du document.*/
	unconnectDoc(): void

	/**
	 * Modifie les paramètres de recherche.
	 * undefined pour init par défaut (construit le diff si pas de selection user utile).
	 * null pour reset/erase: effacer les diff.
	 */
	setDiff(params: JWedDiffBarParams | null | undefined): void

	/** Construit un objet sérialisable représentant la config de cette session diff. */
	buildDiffParams(): JWedDiffBarParams

	/**
	 * (re)exécution du diff (refresh).
	 * @param fullRefresh si provient du bouton de refresh général de la barre de diff.
	 */
	execDiff(fullRefresh?: boolean): Promise<void>

	/** Impl partielle de IView */
	onViewShown?(): void;

	/** Impl partielle de IView */
	onViewHidden?(closed?: boolean): void;
}


/**
 * Impl de base pour l'action de choix du panel de diff.
 */
export abstract class DiffBarPanelAction extends Action<IWedDiffBar> implements IDiffBarAction {

	isToggle(ctx: IWedDiffBar): this is IActionToggle<IWedDiffBar> {return true}

	/** null pour l'état 'mixed'. */
	getDatas(api: 'toggle', ctx: IWedDiffBar): boolean | null {return ctx.currentPanel?.action === this}

	async execute(ctx: IWedDiffBar, ev?: Event) {
		let panel = ctx.getPanelFor(this);
		if (!panel) panel = await this.buildPanel(ctx);
		ctx.setCurrentPanel(panel);
		panel.setDiff(ctx.initParams);
	}

	abstract buildPanel(ctx: IWedDiffBar, ev?: Event): Promise<IDiffBarPanel>
}

/**
 * Objet à instancier et à associer à un IReg.env
 * pour coordonner le diff par défaut à afficher lors
 * de l'affichage d'un éditeur.
 */
export class WedDiffCoordinator {

	protected params?: JWedDiffBarParams;

	/** Limite de validité de ce params (cas où une ouverture planifiée a échouée). */
	protected deadline: number;

	getParams(): JWedDiffBarParams | null {
		let p = this.params;
		if (p) {
			if (this.deadline > 0 && Date.now() > this.deadline) {
				p = null;
				this.deadline = 0;
			}
			this.params = null;
		}
		return p;
	}

	setParamsOnce(params: JWedDiffBarParams) {
		this.params = params;
		this.deadline = Date.now() + 3000;
	}
}

export interface IWedDiffCoordinatorPointer {
	wedDiffCoord?: WedDiffCoordinator
}